import { useEffect, useState, type KeyboardEvent } from 'react';
import { api, oauthStartUrl } from '../lib/api';
import { InviteCard } from '../components/InviteCard';
import { capturePromoCode } from '../lib/promo';
import './Pair.css';

/**
 * The one Google button. It renders in BOTH the idle block and the open
 * sign-in / register form: an account created through Google has no Pear
 * password, so "Have an account? Sign in" must not take the Google route away
 * — that is the only path that strands a Google-created user. The two states
 * are mutually exclusive, so `btn-google-signin` is always exactly one element
 * in the DOM and existing probes keep finding it.
 */
function GoogleSignInButton({ busy }: { busy: boolean }) {
  return (
    <button
      type="button"
      className="pear-btn pear-btn-blush pear-btn-block"
      id="btn-google-signin"
      disabled={busy}
      onClick={() => {
        window.location.href = oauthStartUrl();
      }}
    >
      Continue with Google
    </button>
  );
}

interface PairProps {
  pendingCode: string | null;
  busy: boolean;
  /** Card j1pvawhlLBks — why the last create/join failed, already worded for
   *  the person. Was silent before: an expired invite (ten-minute TTL) made the
   *  join button do nothing visible at all. */
  pairError: string | null;
  /** Re-issues the invite window for an unpaired couple. */
  onRefreshInvite: () => Promise<{ ok: boolean; expiresAt?: number; message?: string }>;
  onClearPairError: () => void;
  authEmail: string | null;
  onCreate: (displayName: string) => void;
  onJoin: (code: string, displayName: string) => void;
  /** Card tJdisOEkVwha — join through the emailed 24-hour LINK instead. */
  onJoinViaLink: (link: string, displayName: string) => void;
  /** The emailed link this device arrived on (?invite=…), opaque to the app. */
  invitedLink: string | null;
  onSetInvitedLink: (link: string | null) => void;
  /** What the worker says about that link: whose couple, and is it still live. */
  linkInfo: { ok: boolean; reason: string | null; handle: string | null; expiresAt: number | null } | null;
  onRefreshLinkInfo: (link: string) => void;
  onSignIn: (email: string, password: string) => Promise<string | null>;
  onRegister: (email: string, password: string, name?: string) => Promise<string | null>;
  /** /api/auth/config().oauth — the Google button only shows when the worker
   *  has its client id + secret, so a click never lands on a 503. */
  oauthEnabled: boolean;
  /** Message from an OAuth bounce (?oauth=denied / state / unverified…). */
  oauthNotice: string | null;
  /** /api/auth/config — the mail controls exist only when the worker can send:
   *  verifyEmail and forgotPassword drive the two new forms. */
  verifyEnabled: boolean;
  forgotEnabled: boolean;
  /** /api/auth/config -> signups. False (PEAR_SIGNUP_MODE not 'open') hides the
   *  create path and points at the waitlist instead: offering a form the worker
   *  refuses is worse than not offering it. Sign-in is unaffected. */
  signupsOpen: boolean;
  /** One-shot line from a mail flow (confirmation sent / link sent…). */
  mailNotice: string | null;
  /** The invite this device arrived on (UX review item 3). null for a device
   *  that was not invited — it opens on the create path, exactly as before. */
  invitedCode: string | null;
  /** Did this device ARRIVE on an invite? Sticky layout flag (see `invited`
   *  below) — separate from invitedCode, which can go null mid-typing. */
  invitedLayout: boolean;
  /** Persist the invite (or drop it, with null) so it survives a reload and
   *  the Google sign-in round trip. Owned by useSession. */
  onSetInvitedCode: (code: string | null) => void;
  /** ?reset_token= from a password-reset link -> open the new-password form. */
  resetToken: string | null;
  /** accounts.emailVerified for the signed-in address. null = unknown, so the
   *  confirm block shows only while the address is known to be unconfirmed. */
  emailVerified: boolean | null;
  /** Returns a display line, plus the dev-only link when no mail left the
   *  machine. Never rejects — the API answers the same for any address. */
  onForgotPassword: (email: string) => Promise<{ ok: boolean; message: string; devLink?: string }>;
  /** Burns the reset token. Returns null on success, an error line otherwise. */
  onApplyReset: (token: string, password: string) => Promise<string | null>;
  onResendVerify: (email?: string) => Promise<string>;
  onClearMailNotice: () => void;
  /** Card Vn4Up6kLOCc3 — who this device was, when there is no session to
   *  say so. Shown only while signed OUT; recognition, not authority. */
  lastIdentity: { email: string; name?: string } | null;
  /** "Not you?" — drop the remembered identity from this browser. */
  onForgetIdentity: () => void;
}

/**
 * Pair / join screen — rewritten invite flow.
 * When a pair code exists it becomes the hero element (copy + share).
 * When absent the original form stays unchanged.
 * Account-aware (epic d1Zu9d4KosuX): a "Have an account?" sign-in block sits
 * above create/join. A signed-in partner who already has a couple goes
 * straight into the room on login (coupleToken in the login response); one
 * without a couple lands here, and create/join links their account.
 */
export function Pair({
  pendingCode,
  busy,
  pairError,
  onRefreshInvite,
  onClearPairError,
  authEmail,
  onCreate,
  onJoin,
  onJoinViaLink,
  invitedLink,
  onSetInvitedLink,
  linkInfo,
  onRefreshLinkInfo,
  onSignIn,
  onRegister,
  oauthEnabled,
  oauthNotice,
  verifyEnabled,
  forgotEnabled,
  signupsOpen,
  mailNotice,
  invitedCode,
  invitedLayout,
  onSetInvitedCode,
  resetToken,
  emailVerified,
  onForgotPassword,
  onApplyReset,
  onResendVerify,
  onClearMailNotice,
  lastIdentity,
  onForgetIdentity,
}: PairProps) {
  const [displayName, setDisplayName] = useState('');
  // An invited device re-opens with the code it was given (item 3): the value
  // came from ?pair_code= on arrival and now lives in the session, so a reload
  // — or the return leg of a Google sign-in, which comes back through the
  // worker with no query of its own — prefills the field again instead of
  // asking the partner to retype a code they never typed once.
  const [code, setCode] = useState(() => invitedCode ?? '');
  /** One job on screen for a partner who arrived on a link (item 3): joining
   *  is the primary action and creating is the alternative offered after it.
   *  The flag itself is sticky in the session (a ?pair_code= sets it and only
   *  a completed pairing clears it), so clearing the box to fix a typo never
   *  rearranges the form under the person typing into it. */
  const invited = invitedLayout;
  /** Why the code on screen cannot be used, in words (item 3): an inline line
   *  under the field replaces the alert() that used to interrupt the partner
   *  mid-typing — and which a browser with alerts suppressed did not show at
   *  all, so a short code just looked ignored. */
  const [codeError, setCodeError] = useState<string | null>(null);
  // Sign-in / register state
  const [authMode, setAuthMode] = useState<'none' | 'signin' | 'register' | 'forgot' | 'reset'>('none');
  /* Invite-only, and the flag arrives a beat after the form can: /api/auth/config
     resolves after first paint, so a device that already had the register form
     open (or the browser's restored state) must collapse it the moment the
     worker says signups are shut. Fails CLOSED, like the worker itself. */
  useEffect(() => {
    if (!signupsOpen && authMode === 'register') setAuthMode('signin');
  }, [signupsOpen, authMode]);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [name, setName] = useState('');
  // Card KpYLZuhhlcvC — the promo-code field on account creation. The code is
  // captured here (typed before the account exists) and redeemed once the
  // device holds a couple token, exactly like a referral link.
  const [promo, setPromo] = useState('');
  const [authError, setAuthError] = useState<string | null>(null);
  // Mail flows: the confirm block's status line, the forgot/reset messages
  // (mailLink is the development-only link — production sends a real email),
  // and the two fields of the new-password form.
  const [verifyMsg, setVerifyMsg] = useState<string | null>(null);
  const [mailMsg, setMailMsg] = useState<string | null>(null);
  const [mailLink, setMailLink] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');

  // "Email the code" (card AnqHIFLf6a5y) — the third way out of the invite
  // block, beside copy and share. Mail must actually be wired for it to show:
  // verifyEnabled and forgotEnabled are both /api/auth/config -> mailEnabled().
  const mailCanSend = verifyEnabled || forgotEnabled;
  /** Mirrors INVITE_ALPHABET in shared/types.ts (the web bundle types its own
   *  surface rather than importing shared/types — same reason RevealHistory
   *  keeps its own copy). */
  const INVITE_CODE_RE = /^[ABCDEFGHJKMNPQRTVWXYZ23456789]{6}$/;

  /** An emailed invite lands on ?pair_code= — fill the join field and strip
   *  the query, the way the mail tokens do in useSession, so a reload cannot
   *  replay it and the recipient never types the code by hand.
   *
   *  Item 3: the code is also written into the session, which is what carries
   *  it across the Google sign-in round trip (a full page navigation that
   *  returns through the worker with no query). Without it the recipient lost
   *  the invite at the exact moment they were being asked to create an account
   *  to use it — the invite intent did not survive the thing it had to
   *  survive. A late ?pair_code= always wins over the remembered one. */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const invited = params.get('pair_code');
    if (!invited) return;
    params.delete('pair_code');
    const qs = params.toString();
    window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash);
    const normalized = invited.trim().toUpperCase();
    if (INVITE_CODE_RE.test(normalized)) {
      setCode(normalized);
      onSetInvitedCode(normalized);
    }
  }, [onSetInvitedCode]);

  /* Card tJdisOEkVwha — an emailed invite LINK arrives on ?invite=… Same
     treatment as the code above and for the same reasons: strip the query so a
     reload cannot replay it, remember it so it survives the Google sign-in
     round trip, and ask the worker what it is — this client cannot verify the
     signature or the single-use ledger, so the handle and the live/dead answer
     both come from /api/pair/link-info. */
  useEffect(() => {
    const params = new URLSearchParams(window.location.search);
    const link = params.get('invite');
    if (link) {
      params.delete('invite');
      const qs = params.toString();
      window.history.replaceState({}, '', window.location.pathname + (qs ? `?${qs}` : '') + window.location.hash);
      onSetInvitedLink(link);
    }
    const current = link ?? invitedLink;
    if (current) onRefreshLinkInfo(current);
  }, [onSetInvitedLink, onRefreshLinkInfo, invitedLink]);

  // A password-reset link opens the new-password form directly: the person
  // holding that link cannot sign in, so offering them sign-in first is a trap.
  useEffect(() => {
    if (resetToken) setAuthMode('reset');
  }, [resetToken]);

  // Copy / share helpers moved into components/InviteCard.tsx — one card
  // renders them on both the Pair screen and the chat share bar, so the
  // URL, the cancellation rule and the failure line cannot drift apart.

  // Actions ---------------------------------------------------------------
  const create = () => {
    if (busy) return;
    onCreate(displayName.trim() || 'Anonymous');
  };

  const join = () => {
    if (busy) return;
    const normalized = code.trim().toUpperCase();
    /* Card tJdisOEkVwha — a device that arrived on an emailed LINK joins with
       the link: it is single-use, it is good for 24 hours, and it needs nothing
       typed. Typing a code switches back to the code path, so the fallback the
       card asks for is always one keystroke away — and a device holding both
       (a link, plus a code someone read out) is never stuck. */
    if (invitedLink && !normalized) {
      setCodeError(null);
      onJoinViaLink(invitedLink, displayName.trim() || 'Anonymous');
      return;
    }
    // Same alphabet the worker checks (item 3): length alone let a code with
    // an ambiguous character (0/O, 1/I/L) through to the API, where it failed
    // as "no couple with that code" — a message that blames the invite rather
    // than the typo. Catch it at the field, in the pair's own words.
    if (!INVITE_CODE_RE.test(normalized)) {
      setCodeError('That is not a full invite code — it is 6 letters and numbers, like ABC23D.');
      return;
    }
    setCodeError(null);
    onJoin(normalized, displayName.trim() || 'Anonymous');
  };

  const onNameKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') (code.length === 6 ? join : create)();
  };
  /** Typing in the code field is also the invite on this device (item 3):
   *  whatever is in the box is what will be submitted, so a remembered code
   *  that the partner overwrites stops being the value that survives the next
   *  reload or the Google round trip. A valid code is remembered; anything
   *  else — including an emptied box — drops it, so a stale invite never
   *  comes back to prefill a field the person has deliberately cleared. */
  const onCodeTyped = (raw: string) => {
    const next = raw.toUpperCase();
    setCode(next);
    setCodeError(null);
    // A new code is a new attempt: the last failure stops talking
    // (card j1pvawhlLBks).
    if (pairError) onClearPairError();
    onSetInvitedCode(INVITE_CODE_RE.test(next) ? next : null);
  };
  const onCodeKey = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter') join();
  };

  // Auth actions -----------------------------------------------------------
  /** A recognised device resumes with ONE action: the address is already in the
   *  notice above, so "Sign in" opens the form with it filled rather than making
   *  the person retype what the screen just told them. */
  const resumeSignIn = () => {
    if (lastIdentity?.email) setEmail(lastIdentity.email);
    toggleAuth('signin');
  };

  const toggleAuth = (mode: 'signin' | 'register') => {
    // Invite-only: a register form must not be reachable even if a stale link or
    // an old walk clicks the old button.
    if (mode === 'register' && !signupsOpen) mode = 'signin';
    setAuthMode(cur => (cur === mode ? 'none' : mode));
    setAuthError(null);
    setMailMsg(null);
    setMailLink(null);
  };

  /** Prefill the address everywhere it is asked for — after registering, the
   *  confirm block and any later form start from what the person just typed. */
  useEffect(() => {
    if (authEmail && !email) setEmail(authEmail);
  }, [authEmail, email]);

  /** Card Vn4Up6kLOCc3 — recognised but signed out: the address this device
   *  last used is offered in the box instead of typed again. Filled ONCE per
   *  remembered identity (and only while there is no live session — the
   *  effect above owns that case), so clearing the field stays an edit the
   *  person made rather than something the screen keeps undoing. */
  const [prefilledFor, setPrefilledFor] = useState<string | null>(null);
  useEffect(() => {
    const want = authEmail ? null : lastIdentity?.email ?? null;
    if (want === prefilledFor) return;
    setPrefilledFor(want);
    if (want) setEmail(cur => cur || want);
  }, [authEmail, lastIdentity, prefilledFor]);

  /** The couple's handle for the invite on screen (card w8Mj4ThRii6Z): the
   *  name the joining partner sees before either of them has signed in. No
   *  handle -> no line; a wrong name is worse than none. */
  const [inviteHandle, setInviteHandle] = useState<string | null>(null);
  /* Card j1pvawhlLBks — when this invite runs out. The code dies 24 hours
     after create (card HpgPwz4VbBRa decision (a)), and this is the one call the pair screen already makes with
     the code in hand, so the expiry rides back with the handle and the screen
     can say "expired" instead of showing a code that no longer pairs. */
  const [inviteExpiresAt, setInviteExpiresAt] = useState<number | null>(null);
  useEffect(() => {
    let live = true;
    setInviteHandle(null);
    setInviteExpiresAt(null);
    if (!pendingCode) return;
    api<{ handle: string | null; expiresAt?: number }>(`/api/pair/handle?code=${encodeURIComponent(pendingCode)}`)
      .then(d => {
        if (live) {
          setInviteHandle(d?.handle ?? null);
          setInviteExpiresAt(d?.expiresAt ?? null);
        }
      })
      .catch(() => {
        /* the invite still works without a name — never show a wrong one */
      });
    return () => {
      live = false;
    };
  }, [pendingCode]);

  /** The couple behind the invite this device was SENT (item 3). Same route,
   *  same credential rule (the code in hand fetches the name), but keyed on
   *  the recipient's code rather than the creator's own — the review named
   *  that mix-up: the partner was naming the wrong couple. No handle -> no
   *  line; a wrong name is worse than none. */
  const [invitedHandle, setInvitedHandle] = useState<string | null>(null);
  useEffect(() => {
    let live = true;
    setInvitedHandle(null);
    if (!invitedCode) return;
    api<{ handle: string | null }>(`/api/pair/handle?code=${encodeURIComponent(invitedCode)}`)
      .then(d => {
        if (live) setInvitedHandle(d?.handle ?? null);
      })
      .catch(() => {
        /* the invite still works without a name — never show a wrong one */
      });
    return () => {
      live = false;
    };
  }, [invitedCode]);

  /** Card tJdisOEkVwha — the name for whichever invite this device holds: the
   *  code's lookup above, or the LINK's own answer from /api/pair/link-info.
   *  Declared after both sources so it reads whichever one is live. */
  const inviteName = invitedHandle ?? (invitedLink ? linkInfo?.handle ?? null : null);

  /** Ask for a reset link. The API's answer is identical for every address,
   *  so this never confirms whether an account exists. */
  const submitForgot = async () => {
    const addr = email.trim();
    if (!addr) {
      setAuthError('Enter your email address.');
      return;
    }
    setAuthError(null);
    const r = await onForgotPassword(addr);
    setMailMsg(r.message);
    setMailLink(r.devLink ?? null);
  };

  /** The other half of the reset link: set the new password, then sign in. */
  const submitReset = async () => {
    if (newPassword.length < 10) {
      setAuthError('Password must be at least 10 characters.');
      return;
    }
    if (newPassword !== confirmPassword) {
      setAuthError('Both passwords must match.');
      return;
    }
    setAuthError(null);
    const err = await onApplyReset(resetToken ?? '', newPassword);
    if (err) {
      setAuthError(err);
      return;
    }
    setNewPassword('');
    setConfirmPassword('');
    setAuthMode('signin');
  };

  const submitVerify = async () => {
    setVerifyMsg(null);
    setVerifyMsg(await onResendVerify(email.trim() || undefined));
  };

  const submitAuth = async () => {
    if (busy) return;
    setAuthError(null);
    const pw = password;
    if (authMode === 'register' && pw.length < 10) {
      setAuthError('Password must be at least 10 characters.');
      return;
    }
    // Card KpYLZuhhlcvC — capture any typed promo code with the registration,
    // so it can be redeemed the moment this device's couple is formed.
    if (authMode === 'register') capturePromoCode(promo);
    const ok =
      authMode === 'signin'
        ? await onSignIn(email.trim(), pw)
        : await onRegister(email.trim(), pw, name.trim() || undefined);
    if (!ok) {
      // onSignIn/onRegister return null on any failure (bad credentials,
      // network, duplicate). For register, re-ask a firing password so the
      // user can correct rather than guess.
      setAuthError(
        authMode === 'signin'
          ? 'That email or handle and password did not match. Try again.'
          : 'Could not create that account. Check the email and try a different password.',
      );
    } else {
      // Success: if the sign-in returned a coupleToken, App already flipped
      // to chat. Registering without a couple stays on this screen (auth
      // email shown) so the user can create/join.
      setEmail('');
      setPassword('');
      setName('');
      setPromo('');
      setAuthMode('none');
    }
  };

  return (
    <section className="pear-pair" id="pair-screen">
      {/* The tagline "Two of you. One space to remember." moved UP to the
          landing's final beat, unbolded (founder email 2026-10-19). */}

      {/* ── Invite block (hero code) — shown when partner already created ─
          One card shared with the chat share bar (components/InviteCard):
          Copy / Share / Email / QR, then the waiting line and the couple's
          handle. The heading, code, buttons and handle ids are unchanged. */}
      {pendingCode && (
        <InviteCard
          code={pendingCode}
          mailEnabled={mailCanSend}
          idPrefix="pair-screen-qr"
          variant="pair"
          inviteHandle={inviteHandle}
          /* Card j1pvawhlLBks — the invite dies 24 hours after create, so
             the card that shows it is the card that must be able to revive it
             or say it is dead. */
          expiresAt={inviteExpiresAt}
          onRefresh={onRefreshInvite}
          onRefreshed={setInviteExpiresAt}
        />
      )}

      {/* ── Sign in / register ─────────────────────────────────────── */}
      <div className="pear-auth-block">
        {/* While signups are closed, the permanent invite note below says the
            only thing a failed Google sign-in could say — and said it twice,
            which is exactly what the screen looked like. The notice returns the
            moment the door opens, when it carries new information again. */}
        {oauthNotice && signupsOpen && (
          <p className="pear-auth-error" role="alert" id="oauth-notice">
            {oauthNotice}
          </p>
        )}
        {mailNotice && (
          <p className="pear-auth-mailmsg" role="status" id="mail-notice">
            {mailNotice}
          </p>
        )}
        {authEmail && (
          <p className="pear-auth-signedin" role="status" id="signedin-notice">
            Signed in as <strong>{authEmail}</strong>{authMode === 'none' && ' — create or join your couple below.'}
          </p>
        )}
        {/* Card Vn4Up6kLOCc3 — the landing screen recognises the person who
            signed out without remembering them IN: email and name only, no
            session, no couple, nothing here can open anything. "Not you?"
            clears it so a shared browser can stop being someone's device. */}
        {!authEmail && lastIdentity && (
          <p className="pear-auth-recognized" role="status" id="recognized-notice">
            Welcome back{lastIdentity.name ? `, ${lastIdentity.name}` : ''} — this device last
            signed in as <strong>{lastIdentity.email}</strong>.{' '}
            <button
              type="button"
              className="pear-auth-notyou"
              id="btn-not-you"
              onClick={onForgetIdentity}
            >
              Not you?
            </button>
          </p>
        )}
        {!authEmail && lastIdentity && authMode === 'none' && (
          <div className="pear-auth-actions" id="resume-signin">
            <button
              type="button"
              className="pear-btn pear-btn-mustard pear-btn-block"
              id="btn-signin-remembered"
              onClick={resumeSignIn}
            >
              Sign in
            </button>
          </div>
        )}
        {verifyEnabled && authEmail && emailVerified === false && authMode === 'none' && (
          <div className="pear-auth-form" id="verify-form">
            <p className="pear-auth-verify-copy" id="verify-copy">
              Confirm your email to finish setting up. We sent a link to <strong>{authEmail}</strong>.
            </p>
            <input
              type="email"
              id="input-verify-email"
              placeholder="Email"
              className="pear-chat-input pear-input-center"
              aria-label="Email to confirm"
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
            <div className="pear-auth-actions">
              <button
                type="button"
                className="pear-btn pear-btn-mustard pear-btn-block"
                id="btn-verify-resend"
                disabled={busy}
                onClick={() => void submitVerify()}
              >
                Send confirmation link
              </button>
            </div>
            {verifyMsg && (
              <p className="pear-auth-mailmsg" role="status" id="verify-msg">
                {verifyMsg}
              </p>
            )}
          </div>
        )}
        {authMode === 'none' && !authEmail && (lastIdentity || signupsOpen) && (
          <p className="pear-auth-links">
            {!lastIdentity && (
              <button type="button" className="pear-auth-link" id="btn-go-signin" onClick={() => toggleAuth('signin')}>
                Have an account? Sign in
              </button>
            )}
            {!lastIdentity && signupsOpen && <span className="pear-auth-or">·</span>}
            {signupsOpen && (
              <button type="button" className="pear-auth-link" id="btn-go-register" onClick={() => toggleAuth('register')}>
                Create an account
              </button>
            )}
          </p>
        )}
        {/* Invitations are handed out by Jonathan, so the app does not publish
            the waitlist address: it says the door is shut and leaves the route
            in to whoever gave them the link. */}
        {authMode === 'none' && !authEmail && !signupsOpen && (
          <p className="pear-auth-invite" id="signup-closed-note">
            Pear is opening up a few at a time — an invitation is needed to join.
          </p>
        )}
        {authMode === 'none' && !authEmail && oauthEnabled && <GoogleSignInButton busy={busy} />}
        {(authMode === 'signin' || authMode === 'register') && (
          <div className="pear-auth-form" id="auth-form">
            {oauthEnabled && (
              <>
                <GoogleSignInButton busy={busy} />
                <p className="pear-auth-divider">or</p>
              </>
            )}
            {authMode === 'register' && (
              <input
                type="text"
                id="input-auth-name"
                placeholder="Your name (optional)"
                className="pear-chat-input pear-input-center"
                aria-label="Name"
                value={name}
                onChange={e => setName(e.target.value)}
              />
            )}
            {authMode === 'register' && (
              <input
                type="text"
                id="input-auth-promo"
                placeholder="Promo code (optional)"
                className="pear-chat-input pear-input-center"
                aria-label="Promo code"
                autoComplete="off"
                value={promo}
                onChange={e => setPromo(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && void submitAuth()}
              />
            )}
            <input
              type={authMode === 'signin' ? 'text' : 'email'}
              id="input-auth-email"
              placeholder={authMode === 'signin' ? 'Email or handle' : 'Email'}
              className="pear-chat-input pear-input-center"
              aria-label={authMode === 'signin' ? 'Email or handle' : 'Email'}
              autoComplete="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
            />
            <input
              type="password"
              id="input-auth-password"
              placeholder={authMode === 'register' ? 'Password (10+ characters)' : 'Password'}
              className="pear-chat-input pear-input-center"
              aria-label="Password"
              autoComplete={authMode === 'register' ? 'new-password' : 'current-password'}
              value={password}
              onChange={e => setPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && void submitAuth()}
            />
            {authError && (
              <p className="pear-auth-error" role="alert">
                {authError}
              </p>
            )}
            {authMode === 'signin' && forgotEnabled && (
              <p className="pear-auth-links">
                <button
                  type="button"
                  className="pear-auth-link"
                  id="btn-forgot"
                  onClick={() => {
                    setAuthMode('forgot');
                    setAuthError(null);
                    setMailMsg(null);
                    setMailLink(null);
                  }}
                >
                  Forgot your password?
                </button>
              </p>
            )}
            <div className="pear-auth-actions">
              <button
                type="button"
                className="pear-btn pear-btn-mustard pear-btn-block"
                id="btn-auth-submit"
                disabled={busy}
                onClick={() => void submitAuth()}
              >
                {authMode === 'register' ? 'Create account' : 'Sign in'}
              </button>
              <button type="button" className="pear-auth-link" onClick={() => setAuthMode('none')}>
                Cancel
              </button>
            </div>
          </div>
        )}

        {/* ── Forgot password: ask for a reset link ─────────────────────── */}
        {forgotEnabled && authMode === 'forgot' && (
          <div className="pear-auth-form" id="forgot-form">
            <p className="pear-auth-verify-copy" id="forgot-copy">
              Enter your email and we will send a link to set a new password.
            </p>
            <input
              type="email"
              id="input-forgot-email"
              placeholder="Email"
              className="pear-chat-input pear-input-center"
              aria-label="Email for reset link"
              autoComplete="email"
              value={email}
              onChange={e => setEmail(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && void submitForgot()}
            />
            {authError && (
              <p className="pear-auth-error" role="alert">
                {authError}
              </p>
            )}
            <div className="pear-auth-actions">
              <button
                type="button"
                className="pear-btn pear-btn-mustard pear-btn-block"
                id="btn-forgot-submit"
                disabled={busy}
                onClick={() => void submitForgot()}
              >
                Send reset link
              </button>
              <button
                type="button"
                className="pear-auth-link"
                id="btn-forgot-cancel"
                onClick={() => {
                  setAuthMode('signin');
                  setAuthError(null);
                }}
              >
                Back to sign in
              </button>
            </div>
            {mailMsg && (
              <p className="pear-auth-mailmsg" role="status" id="forgot-msg">
                {mailMsg}
              </p>
            )}
            {mailLink && (
              <p className="pear-auth-devlink" id="forgot-devlink">
                Development build (no mail sent):{' '}
                <a href={mailLink} id="link-forgot-dev">
                  {mailLink}
                </a>
              </p>
            )}
          </div>
        )}

        {/* ── Reset password: the second half of the mailed link ─────────── */}
        {authMode === 'reset' && resetToken && (
          <div className="pear-auth-form" id="reset-form">
            <p className="pear-auth-verify-copy" id="reset-copy">
              Choose a new password. It replaces the old one and signs out every device.
            </p>
            <input
              type="password"
              id="input-reset-password"
              placeholder="New password (10+ characters)"
              className="pear-chat-input pear-input-center"
              aria-label="New password"
              autoComplete="new-password"
              value={newPassword}
              onChange={e => setNewPassword(e.target.value)}
            />
            <input
              type="password"
              id="input-reset-confirm"
              placeholder="Repeat the new password"
              className="pear-chat-input pear-input-center"
              aria-label="Repeat new password"
              autoComplete="new-password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              onKeyDown={e => e.key === 'Enter' && void submitReset()}
            />
            {authError && (
              <p className="pear-auth-error" role="alert" id="reset-error">
                {authError}
              </p>
            )}
            <div className="pear-auth-actions">
              <button
                type="button"
                className="pear-btn pear-btn-mustard pear-btn-block"
                id="btn-reset-submit"
                disabled={busy}
                onClick={() => void submitReset()}
              >
                Set new password
              </button>
              <button
                type="button"
                className="pear-auth-link"
                id="btn-reset-cancel"
                onClick={() => {
                  setAuthMode('signin');
                  setAuthError(null);
                }}
              >
                Back to sign in
              </button>
            </div>
          </div>
        )}
      </div>

      {/* ── Original form (create / join) ──────────────────────────────────
          Hidden while an account form is open: both ask for "Your name", so with
          the register form up the landing showed the same field twice and the top
          one sat under the last landing beat (card RfH0OR9xTQmF). One job on
          screen — register or pair — and Cancel / success brings this back.
          Card dkwJbbHB8WhX — the code is only ever asked for after an account
          exists. An uninvited visitor with no account gets account entry alone;
          the create/join code UI appears once they register/sign in (it is the
          "surface the code at account creation" beat, card YMBTZE63UZh9, folded).
          An INVITED device keeps the join form on first viewport — the code is
          why they came — but the account gate (SxLB8uGwtVBX) still makes them
          register before the code actually joins anything. */}
      {authMode === 'none' && (authEmail || invited) && (
      <div className="pear-pair-form" data-invited={invited ? 'true' : 'false'}>
        {/* Both paths need a name (item 3). It sits once, above whichever
            action is the point of this screen, so the partner never meets two
            fields asking who they are. */}
        {invited && (
          <p className="pear-invite-lead" id="invite-heading" data-testid="invite-heading">
            {inviteName ? `You're joining ${inviteName}'s pair.` : "You're joining your partner."}
          </p>
        )}

        {/* Card tJdisOEkVwha — say why a link will not work BEFORE the person
            fills in a name and presses Join: a spent or expired link is the one
            failure they can act on (ask for a fresh one / type the code). */}
        {invited && invitedLink && linkInfo && !linkInfo.ok && (
          <p className="pear-invite-lead" role="alert" data-testid="invite-link-state">
            {linkInfo.reason === 'link_used'
              ? 'That invite link has already been used.'
              : linkInfo.reason === 'link_expired'
                ? 'That invite link has expired — emailed links work for 24 hours.'
                : 'That invite link is not valid.'}{' '}
            Ask for a fresh one, or type the pair code below.
          </p>
        )}

        {invited && (
          <div className="pear-pair-group pear-pair-group-primary">
            <input
              type="text"
              id="input-display-name"
              placeholder="Your name"
              className="pear-chat-input pear-input-center"
              aria-label="Display name"
              value={displayName}
              onChange={e => setDisplayName(e.target.value)}
              onKeyDown={onNameKey}
              disabled={busy}
              autoComplete="name"
            />
            <input
              type="text"
              id="input-join-code"
              placeholder="Pair code"
              className="pear-chat-input pear-input-center pear-input-code"
              aria-label="Pair code"
              value={code}
              onChange={e => onCodeTyped(e.target.value)}
              onKeyDown={onCodeKey}
              disabled={busy}
              maxLength={6}
            />
            <button className="pear-btn pear-btn-mustard pear-btn-block" id="btn-join" disabled={busy} onClick={join}>
              Join your partner
            </button>
            {codeError && (
              <p className="pear-pair-error" role="alert" id="code-error" data-testid="code-error">
                {codeError}
              </p>
            )}
            {pairError && (
              <p className="pear-pair-error" role="alert" id="pair-error" data-testid="pair-error">
                {pairError}
              </p>
            )}
            <p className="pear-pair-note" id="pair-gate-note">
              You'll need an account — the code pairs two accounts, it doesn't replace one.
            </p>
          </div>
        )}

        {!invited && (
        <div className="pear-pair-group">
          <input
            type="text"
            id="input-display-name"
            placeholder="Your name"
            className="pear-chat-input pear-input-center"
            aria-label="Display name"
            value={displayName}
            onChange={e => setDisplayName(e.target.value)}
            onKeyDown={onNameKey}
            disabled={busy}
            autoComplete="name"
          />
          <button className="pear-btn pear-btn-mustard pear-btn-block" id="btn-create" disabled={busy} onClick={create}>
            Create a pair
          </button>
        </div>
        )}

        <div className="pear-or">{invited ? 'or, on your own' : 'or'}</div>

        {/* An invited partner gets one job (item 3): joining is the primary
            action and creating is the deliberate alternative, offered after
            rather than racing it. An uninvited device keeps the old order —
            create first — because there is no invite to be diverted from. */}
        {invited ? (
          <div className="pear-pair-group pear-pair-group-quiet">
            <button className="pear-btn pear-btn-quiet pear-btn-block" id="btn-create" disabled={busy} onClick={create}>
              Start a new pair instead
            </button>
          </div>
        ) : (
          <div className="pear-pair-group">
            <input
              type="text"
              id="input-join-code"
              placeholder="Pair code"
              className="pear-chat-input pear-input-center pear-input-code"
              aria-label="Pair code"
              value={code}
              onChange={e => onCodeTyped(e.target.value)}
              onKeyDown={onCodeKey}
              disabled={busy}
              maxLength={6}
            />
            <button className="pear-btn pear-btn-blush pear-btn-block" id="btn-join" disabled={busy} onClick={join}>
              Join with code
            </button>
            {codeError && (
              <p className="pear-pair-error" role="alert" id="code-error" data-testid="code-error">
                {codeError}
              </p>
            )}
            {/* Card j1pvawhlLBks — the failure used to exist only in the console,
                so a dead code looked like a button that did nothing. */}
            {pairError && (
              <p className="pear-pair-error" role="alert" id="pair-error" data-testid="pair-error">
                {pairError}
              </p>
            )}
            <p className="pear-pair-note" id="pair-gate-note">
              You'll need an account — the code pairs two accounts, it doesn't replace one.
            </p>
          </div>
        )}
      </div>
  )}

      {/* Legacy code hint — the creator's own code, kept for backwards compat;
          an invitee has no code of their own to share, so the "share this"
          line would be naming the couple they are about to join as if it were
          theirs (item 3). Card dkwJbbHB8WhX — only a partner who actually has
          an account can be the creator of a couple, so the hint is gated like
          the code form above: no account, no code. */}
      {!invited && authEmail && (
      <div className="pear-code-hint" aria-label="Pair code info">
        <span className="pear-code-label">Share this code with your partner</span>
        <code className="pear-code" id="pair-code">
          {pendingCode ?? '—'}
        </code>
      </div>
      )}
    </section>
  );
}
